import { useRef } from 'react';
import { notification } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  OAuth2ThirdPartyEnum,
  useCacheOAuthAuthorizationCodeMutation,
  useExchangeOAuthAuthorizationCodeForTokensMutation,
  useExchangeOAuthAuthorizationCodeForTokensViaTransientTokenMutation,
  useLogOAuthAuthorizationErrorMutation,
} from '@/types/generated/graphql';
import { TRANSIENT_TOKEN } from '../constants/integration.constants';
import { cacheAddIntegration, cacheIncrementIntegrationCount } from '../integration-handler';
import { ExecuteMutationOptions } from '../integrations.type';
import Cookies from 'js-cookie';
import { cookieOptions } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';

const executeMutation = async <TInput>({ canRun, input, mutation, isRunningRef }: ExecuteMutationOptions<TInput>) => {
  if (!canRun || isRunningRef.current) return;
  isRunningRef.current = true;
  await mutation({ variables: { input } });
};

export const useThirdPartyOAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRunningRef = useRef(false);
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const thirdPartyName = capitalize(searchParams.get('thirdPartyName') || 'Pipedrive');
  const transientToken = Cookies.get(TRANSIENT_TOKEN);
  const { t } = useTranslation();

  const navigateToIntegrations = () => {
    navigate('/settings/integrations?tab=all');
    isRunningRef.current = false;
  };

  const [logOAuthAuthorizationError] = useLogOAuthAuthorizationErrorMutation({
    onCompleted: () => {
      navigateToIntegrations();
    },
    onError: () => {
      navigateToIntegrations();
    },
  });

  const [cacheOAuthAuthorizationCode] = useCacheOAuthAuthorizationCodeMutation({
    onCompleted: (data) => {
      Cookies.set(
        TRANSIENT_TOKEN,
        data?.cacheOAuthAuthorizationCode?.data?.transientToken || '',
        { ...cookieOptions, expires: 5 / (60 * 24) } //transient Token expires for 5 min
      );
      notification.info({
        message: t('please_login_to_complete_pipedrive_integration'),
      });
    },
    onError: (err) => {
      notification.error({ message: err?.message });
    },
  });

  const [exchangeOauthForToken] = useExchangeOAuthAuthorizationCodeForTokensMutation({
    onCompleted: (data) => {
      notification.success({ message: data?.exchangeOAuthAuthorizationCodeForTokens?.message });
      navigateToIntegrations();
    },
    onError: (err) => {
      notification.error({ message: err?.message });
      navigateToIntegrations();
    },
    update: (_, { data }) => {
      const mutationData = data?.exchangeOAuthAuthorizationCodeForTokens?.data;
      const integartionId = data?.exchangeOAuthAuthorizationCodeForTokens?.data?.id;
      cacheIncrementIntegrationCount(mutationData);
      cacheAddIntegration(integartionId);
    },
  });

  const [exchangeViaTransientToken] = useExchangeOAuthAuthorizationCodeForTokensViaTransientTokenMutation({
    onCompleted: (data) => {
      navigateToIntegrations();
      Cookies.remove(TRANSIENT_TOKEN, cookieOptions);
      notification.success({
        message: data?.exchangeOAuthAuthorizationCodeForTokensViaTransientToken?.message,
      });
    },
    onError: (err) => {
      Cookies.remove(TRANSIENT_TOKEN, cookieOptions);
      notification.error({ message: err?.message });
      navigateToIntegrations();
    },
  });

  const handleExchangeOauthForToken = () =>
    executeMutation({
      canRun: !!code,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        code: code || '',
        ...(state && { state }),
      },
      mutation: exchangeOauthForToken,
      isRunningRef,
    });

  const handleLogOAuthAuthorizationError = () =>
    executeMutation({
      canRun: !!error,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        state: state || '',
        error,
      },
      mutation: logOAuthAuthorizationError,
      isRunningRef,
    });

  const handleCacheOAuth = () =>
    executeMutation({
      canRun: !!code,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        code: code || '',
      },
      mutation: cacheOAuthAuthorizationCode,
      isRunningRef,
    });

  const handleExchangeViaTransientToken = () =>
    executeMutation({
      canRun: !!transientToken,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        transientToken,
      },
      mutation: exchangeViaTransientToken,
      isRunningRef,
    });

  return {
    thirdPartyOAuthCode: code,
    handleExchangeOauthForToken,
    handleLogOAuthAuthorizationError,
    handleCacheOAuth,
    handleExchangeViaTransientToken,
  };
};
